import { Capacitor } from '@capacitor/core';
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  deleteToken,
  onMessage,
  isSupported,
  type Messaging,
  type MessagePayload,
} from 'firebase/messaging';

import i18n from '@shared/i18n';
import { apiClient } from '@shared/lib/api/client';

import { firebaseWebConfig, webVapidKey, isWebPushConfigured } from './firebaseConfig';

const DEVICES_ENDPOINT = '/api/v1/notifications/devices/';
const SW_URL = '/firebase-messaging-sw.js';
/**
 * Dedicated scope for the FCM service worker so it never collides with the
 * vite-plugin-pwa Workbox SW (`/sw.js`). Two SWs sharing a scope fight over
 * registration and break token acquisition.
 */
const SW_SCOPE = '/firebase-cloud-messaging-push-scope';
const INSTALL_ID_KEY = 'web_push_install_id';
const DEVICE_PK_KEY = 'web_push_device_pk';

export interface WebPushDevice {
  id: string;
  platform: 'web';
  device_id: string;
  is_active: boolean;
  locale: string;
  last_seen_at: string;
}

/** Stable per-browser install id, persisted in localStorage. */
function getWebDeviceId(): string {
  let id = localStorage.getItem(INSTALL_ID_KEY);
  if (!id) {
    id =
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(INSTALL_ID_KEY, id);
  }
  return `web:${id}`;
}

function getFirebaseApp() {
  return getApps().length ? getApp() : initializeApp(firebaseWebConfig);
}

/**
 * Register the dedicated FCM service worker, passing the public config as query
 * params (a service worker cannot read `import.meta.env`). Public keys only.
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
  const params = new URLSearchParams({
    apiKey: firebaseWebConfig.apiKey,
    projectId: firebaseWebConfig.projectId,
    messagingSenderId: firebaseWebConfig.messagingSenderId,
    appId: firebaseWebConfig.appId,
  });
  return navigator.serviceWorker.register(`${SW_URL}?${params.toString()}`, {
    scope: SW_SCOPE,
  });
}

/** Upsert this browser as a `platform: 'web'` device (same endpoint as native). */
async function registerDevice(token: string): Promise<WebPushDevice> {
  const device = await apiClient.post<WebPushDevice>(DEVICES_ENDPOINT, {
    platform: 'web',
    device_id: getWebDeviceId(),
    push_token: token,
    app_version: '',
    os_version: '',
    locale: i18n.language || 'en',
  });
  localStorage.setItem(DEVICE_PK_KEY, device.id);
  return device;
}

let foregroundUnsub: (() => void) | null = null;

/** Mirror the native contract: foreground messages become `push:received`. */
function wireForeground(messaging: Messaging): void {
  if (foregroundUnsub) return;
  foregroundUnsub = onMessage(messaging, (payload: MessagePayload) => {
    window.dispatchEvent(
      new CustomEvent('push:received', {
        detail: {
          title: payload.notification?.title ?? '',
          body: payload.notification?.body ?? '',
          data: payload.data ?? {},
        },
      }),
    );
  });
}

/** True only on a configured, push-capable web browser (never native). */
export async function webPushSupported(): Promise<boolean> {
  if (Capacitor.isNativePlatform() || !isWebPushConfigured()) return false;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

async function acquireAndRegister(): Promise<boolean> {
  const registration = await registerServiceWorker();
  const messaging = getMessaging(getFirebaseApp());
  const token = await getToken(messaging, {
    vapidKey: webVapidKey,
    serviceWorkerRegistration: registration,
  });
  if (!token) return false;
  await registerDevice(token);
  wireForeground(messaging);
  return true;
}

/**
 * Opt-in entry point — call from a user gesture (browsers reject
 * `Notification.requestPermission()` otherwise). Returns true once a token has
 * been acquired and registered with the backend.
 */
export async function enableWebPush(): Promise<boolean> {
  if (!(await webPushSupported())) return false;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;
  return acquireAndRegister();
}

export async function getCurrentWebPushDevice(): Promise<WebPushDevice | null> {
  if (!(await webPushSupported())) return null;
  const devices = await apiClient.get<WebPushDevice[]>(DEVICES_ENDPOINT);
  const deviceId = getWebDeviceId();
  const currentDevice =
    devices.find(
      (device) =>
        device.platform === 'web' &&
        device.device_id === deviceId &&
        device.is_active,
    ) ?? null;
  if (currentDevice) {
    localStorage.setItem(DEVICE_PK_KEY, currentDevice.id);
  }
  return currentDevice;
}

export async function disableWebPush(): Promise<boolean> {
  if (!(await webPushSupported())) return false;
  const storedPk = localStorage.getItem(DEVICE_PK_KEY);
  const currentDevice = storedPk ? null : await getCurrentWebPushDevice();
  const devicePk = storedPk ?? currentDevice?.id;
  if (devicePk) {
    try {
      await apiClient.delete(`${DEVICES_ENDPOINT}${devicePk}/`);
    } catch {
      // The local browser state may outlive a deleted backend row; continue
      // clearing local push state so re-enable can register cleanly.
    }
  }
  localStorage.removeItem(DEVICE_PK_KEY);

  try {
    const messaging = getMessaging(getFirebaseApp());
    await deleteToken(messaging);
  } catch {
    // Backend deactivation is authoritative for stopping delivery.
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    await registration?.unregister();
  } catch {
    // Re-enable will register a fresh scoped worker.
  }

  return true;
}

/**
 * Best-effort refresh on app load: if the browser already granted permission,
 * re-acquire the token and keep the device row fresh — without prompting.
 */
export async function initWebPushIfGranted(): Promise<void> {
  if (!(await webPushSupported())) return;
  if (Notification.permission !== 'granted') return;
  try {
    await acquireAndRegister();
  } catch {
    // Network/SW hiccup — the Settings opt-in can retry on demand.
  }
}
